export function norm(s) {
  return (s || "").trim().toLowerCase();
}

export function belongsToCampus(journey, campus) {
  const campusId = campus?.id;

  const stopMatch =
    Array.isArray(journey?.stops) &&
    journey.stops.some((s) => s?.campusId === campusId);

  if (stopMatch) return true;

  // fallback for legacy single-campus journeys
  return norm(journey?.campus) === norm(campus?.name);
}

export function candJourneyType(group) {
  const kind = String(group?.groupKind || "").toUpperCase();

  if (kind === "TRANSFER") {
    const key = String(group?.groupKey || "").toUpperCase();
    if (key === "ARRIVALS") return "ARRIVALS";
    if (key === "DEPARTURES") return "DEPARTURES";
    return "ARRIVALS";
  }

  if (kind === "ACTIVITY") return "ACTIVITY";
  if (kind === "WEEKEND") return "WEEKEND";

  return "";
}

export function paxKeyFromPassengerDTO(passenger) {
  const type = String(passenger?.personType || "").toUpperCase();
  const id = passenger?.id;
  return `${type}:${id}`;
}

export function personKey(person) {
  const type = String(person?.personType || "").toUpperCase();

  return type === "ADULT"
    ? `ADULT:${person?.adultId ?? person?.id ?? ""}`
    : `STUDENT:${person?.studentId ?? person?.id ?? ""}`;
}

export function busHasPassenger(bus, key) {
  const passengers = Array.isArray(bus?.passengers) ? bus.passengers : [];
  return passengers.some((p) => personKey(p) === key);
}

export function busHasCampusPickup(bus, campusId) {
  return (
    Array.isArray(bus?.stops) &&
    bus.stops.some(
      (s) =>
        Number(s?.campusId) === Number(campusId) &&
        String(s?.stopType).toUpperCase() === "PICKUP"
    )
  );
}

export function busHasCampusDropoff(bus, campusId) {
  return (
    Array.isArray(bus?.stops) &&
    bus.stops.some(
      (s) =>
        Number(s?.campusId) === Number(campusId) &&
        String(s?.stopType).toUpperCase() === "DROPOFF"
    )
  );
}

export function getCoverageForPerson({ person, buses, campusId, journeyType }) {
  const key = personKey(person);

  let hasPickup = false;
  let hasDropoff = false;

  for (const bus of buses || []) {
    if (!busHasPassenger(bus, key)) continue;

    if (busHasCampusPickup(bus, campusId)) hasPickup = true;
    if (busHasCampusDropoff(bus, campusId)) hasDropoff = true;
  }

  const jt = String(journeyType || "").toUpperCase();

  // One-way logic for transfer days
  if (jt === "DEPARTURES") {
    if (hasPickup) return "full";
    if (hasDropoff) return "partial";
    return "none";
  }

  if (jt === "ARRIVALS") {
    if (hasDropoff) return "full";
    if (hasPickup) return "partial";
    return "none";
  }

  // Round-trip logic for activities / weekends
  if (hasPickup && hasDropoff) return "full";
  if (hasPickup || hasDropoff) return "partial";
  return "none";
}

export function sortJourneys(a, b) {
  const at = String(a?.departTime || "");
  const bt = String(b?.departTime || "");
  if (at !== bt) return at.localeCompare(bt);
  return String(a?.groupName || "").localeCompare(String(b?.groupName || ""));
}

export function buildFilteredJourneys({ journeys, campusList, selectedCampusId }) {
  if (!selectedCampusId) return [];

  if (selectedCampusId === "UNASSIGNED") {
    return (journeys || [])
      .filter((j) => {
        const matched = (campusList || []).some((c) => belongsToCampus(j, c));
        return !matched;
      })
      .sort(sortJourneys);
  }

  const campus = (campusList || []).find(
    (c) => String(c.id) === String(selectedCampusId)
  );

  if (!campus) return [];

  return (journeys || [])
    .filter((j) => belongsToCampus(j, campus))
    .sort(sortJourneys);
}

export function buildAllocatedKeysByJourneyType(filteredJourneys) {
  const map = new Map();

  for (const journey of filteredJourneys || []) {
    const jt = String(journey?.journeyType || "").trim().toUpperCase();
    if (!jt) continue;

    if (!map.has(jt)) map.set(jt, new Set());

    const set = map.get(jt);
    const passengers = Array.isArray(journey?.passengers) ? journey.passengers : [];

    for (const passenger of passengers) {
      const key = paxKeyFromPassengerDTO(passenger);
      if (!key.endsWith(":undefined") && !key.endsWith(":null")) {
        set.add(key);
      }
    }
  }

  return map;
}

export function buildBusesByJourneyType(filteredJourneys) {
  const map = new Map();

  for (const journey of filteredJourneys || []) {
    const jt = String(journey?.journeyType || "").trim().toUpperCase();
    if (!jt) continue;

    const used = Array.isArray(journey?.passengers) ? journey.passengers.length : 0;
    const cap = Number(journey?.capacity) || 0;
    const free = Math.max(0, cap - used);

    if (!map.has(jt)) map.set(jt, []);
    map.get(jt).push({ bus: journey, used, cap, free });
  }

  for (const [, arr] of map.entries()) {
    arr.sort((a, b) =>
      String(a.bus?.departTime || "").localeCompare(String(b.bus?.departTime || ""))
    );
  }

  return map;
}

export function buildFilteredCandidateRows({
  candidateRows,
  selectedCampusId,
  campusList,
}) {
  if (!selectedCampusId) return [];

  if (selectedCampusId === "UNASSIGNED") {
    return (candidateRows || []).filter((r) => !String(r?.campus || "").trim());
  }

  const campusObj = (campusList || []).find(
    (c) => String(c.id) === String(selectedCampusId)
  );

  if (!campusObj) return [];

  const campusName = String(campusObj?.name || "").trim().toLowerCase();

  return (candidateRows || []).filter(
    (r) => String(r?.campus || "").trim().toLowerCase() === campusName
  );
}

export function buildCandidateGroups(filteredCandidateRows) {
  const byKey = new Map();

  for (const row of filteredCandidateRows || []) {
    const key = row.groupKey || "UNKNOWN";

    if (!byKey.has(key)) {
      byKey.set(key, {
        groupKey: key,
        groupName: row.groupName || key,
        groupKind: row.groupKind || "",
        groupSubtype: row.groupSubtype ?? null,
        students: [],
        adults: [],
      });
    }

    const group = byKey.get(key);

    const person = {
      name: row.personName,
      studentId: row.studentId,
      adultId: row.adultId,
      personType: row.personType,
    };

    if (String(row.personType).toUpperCase() === "ADULT") {
      group.adults.push(person);
    } else {
      group.students.push(person);
    }
  }

  for (const group of byKey.values()) {
    group.adults.sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    );
    group.students.sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    );
  }

  const groups = Array.from(byKey.values());

  groups.sort((a, b) => {
    const ak = String(a.groupKind || "");
    const bk = String(b.groupKind || "");
    if (ak !== bk) return ak.localeCompare(bk);
    return String(a.groupName || "").localeCompare(String(b.groupName || ""));
  });

  return groups;
}

export function buildCandidateGroupsWithCoverage({
  candidateGroups,
  filteredJourneys,
  selectedCampusId,
}) {
  return (candidateGroups || []).map((group) => {
    const jt = candJourneyType(group);

    const people = [
      ...(group.adults || []).map((p) => ({ ...p, personType: "ADULT" })),
      ...(group.students || []).map((p) => ({ ...p, personType: "STUDENT" })),
    ];

    const relevantJourneys = (filteredJourneys || []).filter(
      (j) => String(j?.journeyType || "").trim().toUpperCase() === jt
    );

    const campusId =
      selectedCampusId && selectedCampusId !== "UNASSIGNED"
        ? Number(selectedCampusId)
        : null;

    const personCoverage = {};
    let full = 0;
    let partial = 0;
    let none = 0;

    for (const person of people) {
      const key = personKey(person);

      const coverageStatus = getCoverageForPerson({
        person,
        buses: relevantJourneys,
        campusId,
        journeyType: jt,
      });

      personCoverage[key] = coverageStatus;

      if (coverageStatus === "full") full += 1;
      else if (coverageStatus === "partial") partial += 1;
      else none += 1;
    }

    const total = people.length;

    let status = "none";
    if (total > 0 && full === total) status = "full";
    else if (full > 0 || partial > 0) status = "partial";

    return {
      ...group,
      _journeyType: jt,
      personCoverage,
      coverage: {
        total,
        full,
        partial,
        none,
        covered: full,
        status,
      },
    };
  });
}

export function buildSelectedCampusLabel({ selectedCampusId, campusList }) {
  if (!selectedCampusId) return "";
  if (selectedCampusId === "UNASSIGNED") return "Unassigned";

  const campus = (campusList || []).find(
    (c) => String(c.id) === String(selectedCampusId)
  );

  return campus?.name || "";
}

export function buildMissingPassengersForGroup(group) {
  const people = [
    ...(group?.adults || []).map((p) => ({ ...p, personType: "ADULT" })),
    ...(group?.students || []).map((p) => ({ ...p, personType: "STUDENT" })),
  ];

  return people.filter((person) => {
    const key = personKey(person);
    const coverageStatus = group?.personCoverage?.[key] || "none";
    return coverageStatus !== "full";
  });
}

export function buildPassengerPayload(passengers) {
  return (passengers || []).map((p) => ({
    personType: p.personType,
    id: p.id,
  }));
}

export function buildAdditionalPassengersForBus({ bus, missingPeople }) {
  const existing = Array.isArray(bus?.passengers) ? bus.passengers : [];
  const existingKeys = new Set(existing.map(paxKeyFromPassengerDTO));

  return (missingPeople || [])
    .map((person) => {
      const type = String(person.personType).toUpperCase();
      const id =
        type === "ADULT"
          ? person.adultId ?? person.id
          : person.studentId ?? person.id;

      return { personType: type, id };
    })
    .filter((p) => !existingKeys.has(`${p.personType}:${p.id}`));
}

export function limitPassengersToCapacity({ bus, passengersToAdd }) {
  const existing = Array.isArray(bus?.passengers) ? bus.passengers : [];
  const cap = Number(bus?.capacity) || 0;
  const free = Math.max(0, cap - existing.length);

  return {
    free,
    addNow: (passengersToAdd || []).slice(0, free),
  };
}

function normaliseStopTime(val) {
  if (!val) return null;
  const s = String(val);
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s.slice(0, 5);
  if (/^\d{2}:\d{2}$/.test(s)) return s;
  return null;
}

/**
 * Rebuild a full update payload from an existing journey DTO, overriding the
 * driver contact details and optionally flagging confirmation. Mirrors the
 * payload AddEditBusDialog sends on edit so a PUT round-trips every field
 * (used by the "Confirm" action on a journey card, which must persist contact
 * details before the backend's confirm endpoint will accept the bus).
 */
export function buildJourneyUpdatePayload(
  journey,
  { contactName, contactNumber, confirm = false } = {}
) {
  const stops = (Array.isArray(journey?.stops) ? journey.stops : [])
    .slice()
    .sort((a, b) => (a.stopOrder ?? 0) - (b.stopOrder ?? 0))
    .map((s, idx) => ({
      stopOrder: idx + 1,
      location: String(s.location || "").trim() || null,
      campusId:
        s.campusId == null || s.campusId === "" ? null : Number(s.campusId),
      plannedTime: normaliseStopTime(s.plannedTime),
      stopType: s.stopType || null,
    }));

  return {
    groupName: String(journey?.groupName || "").trim(),
    capacity: Number(journey?.capacity),
    campus: journey?.campus ?? "",
    status: journey?.status ?? "Planned",
    meta: {
      journeyType:
        String(journey?.journeyType || "").trim().toUpperCase() || null,
      provider: journey?.provider || null,
      contactName: String(contactName || "").trim() || null,
      contactNumber: String(contactNumber || "").trim() || null,
      clearFields: [],
    },
    stops,
    confirm: Boolean(confirm),
  };
}